import { useMemo, useState } from 'react';
import RowV2 from '@/components/v2/RowV2';
import IconV2 from '@/components/v2/IconV2';
import TextWithEllipsis from '@/components/TextWithEllipsis';
import Skeleton from '@/components/Skeleton';
import EmptyStateNotice from '@/components/EmptyStateNotice';
import FloatingSelectV2 from '@/components/v2/FloatingSelectV2';
import usePagination from '@/hooks/usePagination';
import Pagination from '@/components/pagination/Pagination';
import { FaCaretDown, FaCaretUp } from 'react-icons/fa';
import { VscTelescope } from '@/components/react-icons/main/index.esm';
import useSortableColumns from '@/hooks/useSortableColumns';
import { getRowId } from './utils';
import EditableInputCell from './components/EditableInputCell';
import DateCell from './components/DateCell';
import CheckboxCell from './components/CheckboxCell';
import SelectCell from './components/SelectCell';
import TextCell from './components/TextCell';
import FileCell from './components/FileCell';
import SuggestionCell from './components/SuggestionCell';

const GridTable = ({ rows = [], columns = [], loading = false, emptyStateTitle = 'No records found', emptyStateIcon = VscTelescope, className = '', rowKey = 'id', getRowClassName, portalContainer, initialSort, sortBy: controlledSortBy, sortDirection: controlledSortDirection, onSortChange, enablePagination = false, initialPage = 1, itemsPerPage = 50 }) => {
  const sortableColumns = useMemo(
    () =>
      columns.reduce((acc, column) => {
        if (column.sortable === false) {
          return acc;
        }

        const sortKey = column.sortKey || column.key;

        if (!sortKey) {
          return acc;
        }

        if (column.sortComparator) {
          acc[sortKey] = column.sortComparator;
        }

        return acc;
      }, {}),
    [columns]
  );

  const visibleColumns = useMemo(() => columns.filter((column) => !column.hidden), [columns]);

  const { sortedData, sortBy, sortDirection, handleSort, isSorted } = useSortableColumns(rows, {
    ...(controlledSortBy !== undefined && onSortChange
      ? {
          sortBy: controlledSortBy,
          sortDirection: controlledSortDirection,
          onSortChange
        }
      : {
          initialSortBy: initialSort?.sortBy,
          initialSortDirection: initialSort?.sortDirection || 'asc'
        }),
    columnSortFunctions: sortableColumns
  });

  const gridTemplateColumns = useMemo(
    () =>
      visibleColumns
        .map((column) => {
          if (column.width) {
            return column.width;
          }

          if (column.flex) {
            return `minmax(0, ${column.flex}fr)`;
          }

          return 'minmax(0, 1fr)';
        })
        .join(' '),
    [visibleColumns]
  );

  const [activeSelect, setActiveSelect] = useState(null);

  const pagination = usePagination({
    data: sortedData,
    itemsPerPage: enablePagination ? itemsPerPage : sortedData.length || itemsPerPage,
    initialPage
  });

  const displayedRows = enablePagination ? pagination.paginatedData : sortedData;

  const handleOpenSelect = ({ row, column, cellClass }) => {
    const value = column.getValue ? column.getValue(row, column) : row[column.key];

    setActiveSelect({
      row,
      columnKey: column.key,
      cellClass,
      value
    });
  };

  const activeColumn = useMemo(() => {
    if (!activeSelect) {
      return null;
    }

    return columns.find((column) => column.key === activeSelect.columnKey) || null;
  }, [activeSelect, columns]);

  const selectOptions = activeColumn?.options || [];
  const selectUseSearch = activeColumn?.useSearch ?? false;
  const selectNullable = activeColumn?.nullable ?? false;
  const selectOnSearch = activeColumn?.onSearch;

  const handleSelectChange = (option) => {
    if (!activeSelect || !activeColumn) {
      return;
    }

    if (activeColumn.onChange) {
      const newValue = option ? option.value : null;

      activeColumn.onChange(newValue, {
        row: activeSelect.row,
        column: activeColumn,
        option
      });
    }

    setActiveSelect(null);
  };

  const SortIndicator = ({ column }) => {
    if (!sortBy || sortBy !== column) {
      return null;
    }

    return <IconV2 className="sort-indicator" icon={sortDirection === 'asc' ? FaCaretUp : FaCaretDown} />;
  };

  const renderHeaderCell = (column) => {
    const sortKey = column.sortable === false ? null : column.sortKey || column.key;
    const isSortable = Boolean(sortKey);
    const isActive = isSortable && isSorted(sortKey);

    const handleHeaderClick = () => {
      if (!isSortable) {
        return;
      }

      handleSort(sortKey);
    };

    if (column.renderHeader) {
      return column.renderHeader({ SortIndicator, sortBy, sortDirection, handleSort, isSorted });
    }

    const headerContent = column.displayName ? <TextWithEllipsis>{column.displayName}</TextWithEllipsis> : <span>{column.displayName}</span>;

    if (!isSortable) {
      return (
        <span className={`header-cell ${column.headerClassName || ''}`}>
          {headerContent}
          {column.showSortIndicator && <SortIndicator column={sortKey} />}
        </span>
      );
    }

    return (
      <span className={`sortable-header ${isActive ? 'active' : ''} ${column.headerClassName || ''}`} onClick={handleHeaderClick}>
        {headerContent}
        <SortIndicator column={sortKey} />
      </span>
    );
  };

  const renderCell = (row, column) => {
    const value = column.getValue ? column.getValue(row, column) : row[column.key];

    if (column.renderCell) {
      return column.renderCell({ row, column, value });
    }

    if (column.type === 'text') {
      return <TextCell row={row} column={column} value={value} />;
    }

    if (column.type === 'input') {
      return <EditableInputCell row={row} column={column} value={value} onChange={column.onChange} />;
    }

    if (column.type === 'date') {
      return <DateCell row={row} column={column} value={value} onChange={column.onChange} portalContainer={portalContainer} />;
    }

    if (column.type === 'checkbox') {
      return <CheckboxCell row={row} column={column} value={value} onChange={column.onChange} />;
    }

    if (column.type === 'select') {
      return <SelectCell row={row} column={column} value={value} onOpenSelect={handleOpenSelect} />;
    }

    if (column.type === 'file') {
      return <FileCell row={row} column={column} value={value} />;
    }

    if (column.type === 'suggestion') {
      return <SuggestionCell row={row} column={column} value={value} />;
    }

    if (column.type === 'button') {
      return <div className={`comp-grid-table-cell button-cell ${column.cellClassName || ''}`}>{column.renderButton && column.renderButton({ row, column, value })}</div>;
    }

    return <TextCell row={row} column={column} value={value} />;
  };

  const getRowClasses = (row) => {
    if (!getRowClassName) {
      return '';
    }

    const customClass = getRowClassName(row);

    if (!customClass) {
      return '';
    }

    return customClass;
  };

  return (
    <div className={`comp-grid-table grid-view ${className}`}>
      {loading && <Skeleton count={12} containerStyle={{ marginBottom: '2px' }} skeletonStyle={{ height: 40, borderRadius: 6 }} />}
      {!loading && (!rows || rows.length === 0) && <EmptyStateNotice size="sm" title={emptyStateTitle} icon={emptyStateIcon} />}
      {!loading && rows && rows.length > 0 && sortedData.length === 0 && <EmptyStateNotice size="sm" title="No rows match your filters" icon={emptyStateIcon} />}
      {!loading && sortedData && sortedData.length > 0 && (
        <div className="table-container">
          <div className="table-header comp-grid-table-header grid-layout" style={{ gridTemplateColumns }}>
            {visibleColumns.map((column) => (
              <div key={column.key} className="comp-grid-table-header-cell">
                {renderHeaderCell(column)}
              </div>
            ))}
          </div>
          {displayedRows.map((row) => {
            const id = getRowId(row, rowKey);
            const rowClassName = getRowClasses(row);

            return (
              <RowV2 key={id} className={`comp-grid-table-row grid-layout ${rowClassName}`} style={{ gridTemplateColumns }}>
                {visibleColumns.map((column) => (
                  <div key={column.key} className={`comp-grid-table-cell-wrapper ${column.wrapperClassName || ''}`}>
                    {renderCell(row, column)}
                  </div>
                ))}
              </RowV2>
            );
          })}
        </div>
      )}

      {activeSelect && activeColumn && selectOptions.length > 0 && (
        <FloatingSelectV2
          triggers={[`.${activeSelect.cellClass}`]}
          alignWith={`.${activeSelect.cellClass}`}
          active={!!activeSelect}
          setActive={(isActive) => {
            if (!isActive) {
              setActiveSelect(null);
            }
          }}
          options={selectOptions}
          onChange={handleSelectChange}
          value={activeSelect.value}
          useSearch={selectUseSearch}
          nullable={selectNullable}
          onSearch={selectOnSearch}
        />
      )}

      {enablePagination && !loading && pagination.totalPages > 1 && <Pagination currentPage={pagination.currentPage} totalPages={pagination.totalPages} onPageChange={pagination.goToPage} itemsPerPage={pagination.itemsPerPage} totalItems={pagination.totalItems} className="listing-page-pagination" showInfo={true} />}
    </div>
  );
};

export default GridTable;
